import {useEffect, useRef} from 'react';
import {useIntl} from 'react-intl';
import {Link, useParams, useSearchParams} from 'react-router-dom';
import {useDownloadFile} from '../Hooks/DocumentHooks';
import {getDownloadURL} from '../../../app/pages/documents/api';

const ALLOWED_SYSTEMS = ['internal', 'cis'];

/**
 * Cible des liens partagés vers un document (cf. getShareableFileURL) : la route est protégée,
 * l'utilisateur est donc connecté et autorisé quand le téléchargement est déclenché.
 */
const DocumentDownloadPage = (): JSX.Element => {
  const intl = useIntl();
  const {fileId} = useParams();
  const [searchParams] = useSearchParams();
  const system = searchParams.get('system') ?? 'internal';
  const {mutate, isLoading, isSuccess, isError} = useDownloadFile();
  const hasStarted = useRef(false);

  const isValidLink = !!fileId && /^\d+$/.test(fileId) && ALLOWED_SYSTEMS.includes(system);
  const fileUrl = isValidLink ? getDownloadURL(Number(fileId), system) : '';

  useEffect(() => {
    // Évite un double téléchargement (StrictMode exécute les effets deux fois en dev).
    if (!isValidLink || hasStarted.current) {
      return;
    }
    hasStarted.current = true;
    mutate(fileUrl);
  }, [isValidLink, fileUrl, mutate]);

  return (
    <div className="card">
      <div className="card-body d-flex flex-column align-items-center gap-5 py-15">
        {isValidLink && (isLoading || (!isSuccess && !isError)) && (
          <>
            <span className="spinner-border text-primary" role="status"></span>
            <span className="fs-5">
              {intl.formatMessage({id: 'DOCUMENTS.SHARED_LINK.DOWNLOADING'})}
            </span>
          </>
        )}
        {isSuccess && (
          <>
            <i className="fs-2tx fa-solid fa-circle-check text-success"></i>
            <span className="fs-5">
              {intl.formatMessage({id: 'DOCUMENTS.SHARED_LINK.SUCCESS'})}
            </span>
            <button type="button" className="btn btn-light-primary" onClick={() => mutate(fileUrl)}>
              {intl.formatMessage({id: 'DOCUMENTS.SHARED_LINK.RETRY'})}
            </button>
          </>
        )}
        {(!isValidLink || isError) && (
          <>
            <i className="fs-2tx fa-solid fa-circle-exclamation text-danger"></i>
            <span className="fs-5">{intl.formatMessage({id: 'DOCUMENTS.SHARED_LINK.ERROR'})}</span>
          </>
        )}
        <Link to="/dashboard" className="btn btn-link">
          {intl.formatMessage({id: 'DOCUMENTS.SHARED_LINK.BACK'})}
        </Link>
      </div>
    </div>
  );
};

export default DocumentDownloadPage;
